import { FC, useEffect, useState } from 'react';
import {Modal} from './Modal'
import { Spinner } from 'react-bootstrap';

import { ChatUploadedFile, ChatUploadedFileType } from '../models';

interface Props {
  uploadedFile: ChatUploadedFile
  show: boolean
  handleClose: () => void
  loadUploadedFileBase64Url?: () => void
}

export async function getVideoThumbnailFromURL(videoUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous'; // only needed if loading remote files

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = 0;
    });

    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Failed to get canvas context'));

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnail = canvas.toDataURL('image/png');
      resolve(thumbnail);
    });

    video.addEventListener('error', (err) => {
      reject(new Error('Failed to load video'));
    });

    video.load();
  });
}

const UploadedFile = ({ uploadedFile, handleRemove, loadUploadedFileBase64Url }: { uploadedFile: ChatUploadedFile; handleRemove?: () => void, loadUploadedFileBase64Url?: () => void}) => {

  const [showModal, setShowModal] = useState(false);
  const [thumbnailImageUrl, setThumbnailImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if( uploadedFile.type !== ChatUploadedFileType.AUDIO ){
      if( uploadedFile.thumbnailBase64Url ){
        setThumbnailImageUrl(uploadedFile.thumbnailBase64Url);
      }else if(uploadedFile.objectUrl && uploadedFile.type === ChatUploadedFileType.IMAGE ){
        setThumbnailImageUrl(uploadedFile.objectUrl);
      }
    }
  }, [uploadedFile]);

  const render = () => {
    if (uploadedFile.type === ChatUploadedFileType.AUDIO) {
      return <>
      <audio
        controls
        className="mh-lg-600px"
        controlsList="nodownload"
        src={uploadedFile.objectUrl ? uploadedFile.objectUrl : uploadedFile.base64Url}
      />
      {handleRemove && 
        <button 
          className="btn btn-icon btn-sm text-hover-dark position-absolute mt-n5 me-n5 top-0 end-0" 
          onClick={handleRemove}
        >
          <i className="fa-solid fa-circle-xmark bg-secondary rounded"></i>
        </button>
      }
      </>
    } else {
      return (
        thumbnailImageUrl ? 
        <>
          <img src={thumbnailImageUrl} className={`cursor-pointer img-fluid h-100 rounded`} onClick={()=>setShowModal(true)}/>
          { uploadedFile.type === ChatUploadedFileType.VIDEO &&
            <div className="position-absolute top-50 start-50 translate-middle cursor-pointer" onClick={()=>setShowModal(true)}>
              <i className={`fa-solid fa-play fs-5 text-white`}></i>
            </div>
          }
          {handleRemove && 
            <button 
              className="btn btn-icon btn-sm text-hover-dark position-absolute mt-n5 me-n5 top-0 end-0" 
              onClick={handleRemove}
            >
              <i className="fa-solid fa-circle-xmark bg-secondary rounded"></i>
            </button>
          }
          {showModal && (uploadedFile.base64Url || uploadedFile.objectUrl || loadUploadedFileBase64Url) && <UploadedMediaModal
            show={showModal}
            handleClose={() => setShowModal(false)}
            uploadedFile={uploadedFile}
            loadUploadedFileBase64Url={loadUploadedFileBase64Url}
          />}
        </>
        :
        <Spinner animation='border'/>
      );
    }
  }

  return (uploadedFile.thumbnailBase64Url || uploadedFile.objectUrl || uploadedFile.base64Url) ?
  render()
  : 
  <Spinner animation='border'/>
}

const UploadedMediaModal: FC<Props> = ({ uploadedFile, show, handleClose, loadUploadedFileBase64Url}) => {

  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalSizeClass, setModalSizeClass] = useState('modal-sm');

  const updateModalSize = (width: number, height: number) => {
    const maxHeight = window.innerHeight * 0.95;

    if (height > maxHeight) {
      const ratio = maxHeight / height;
      width *= ratio;
      height = maxHeight;
    }

    if (width < 500) {
      setModalSizeClass('modal-sm');
    } else if (width < 800) {
      setModalSizeClass('modal-md');
    } else if (width < 1140) {
      setModalSizeClass('modal-lg');
    } else {
      setModalSizeClass('modal-xl');
    }
  };

  useEffect(() => {
    setLoading(true);
    if( uploadedFile.objectUrl ){
      setUrl(uploadedFile.objectUrl);
      setLoading(false);
    }else if( uploadedFile.base64Url ){
      setUrl(uploadedFile.base64Url);
      setLoading(false);
    }else if(loadUploadedFileBase64Url){
      const fetchData = async () => {
        await loadUploadedFileBase64Url();
        setLoading(false);
      };
      fetchData();
    }
  }, [uploadedFile]);

  
  return <Modal 
    show={show} 
    handleClose={handleClose} 
    dialogClassName={modalSizeClass}
    modalBodyClassName="overflow-y-auto text-center p-0 rounded">
    {
    loading ?
      <div className="h-180px d-flex justify-content-center align-items-center">
        <Spinner/>
      </div>
    :
    (
      uploadedFile.type === ChatUploadedFileType.IMAGE 
      ?
      url && <img src={url} className="img-fluid" onLoad={(e) => updateModalSize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}/>
      :
      url && <video
          controls
          className="img-fluid"
          style={{marginBottom: "-0.42rem"}}
          controlsList="nodownload"
          autoPlay
          src={url}
          onLoadedMetadata={(e) =>
            updateModalSize(e.currentTarget.videoWidth, e.currentTarget.videoHeight)
          }
        />    
      ) 
    }
    </Modal>
  }
  
  export default UploadedFile